<template>
  <div>
    <div v-if="!errorMessage" class="w-full">
      <input v-model="searchText"
             class="location-search mt-10 mb-0 px-3"
             type="text"
             placeholder="search location name"
             @input.prevent="debounce"
      />
      <loading-icon v-if="areLocationsLoading" />
      <table v-else-if="hasSearchLocations" id="location-table">
        <thead>
          <tr role="row">
            <sort-table-header
              v-for="(value, index) in columns" :key="index"
              ariaControlId="location-table"
              :defaultSort="value.sort"
              :sortId="value.column"
              :text="value.name"
              @sortLocation="sortLocation"
            />
            <th class="text-center pr-0 w-36 whitespace-nowrap">loyalty points enabled</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(location, index) in locations" :key="index" class="location-row">
            <td>{{ location.display_name }}</td>
            <td>{{ location.region }}</td>
            <td :class="{ 'not-editable': !isEditable }" class="location-loyalty-checkbox text-center">
              <sb-checkbox
                :checked="location.loyalty_points"
                :disabled="!isEditable"
                v-on:update:checked="changeLocationsLoyalty(location)"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="text-center">no location found</p>
      <sb-dotted-pagination
        v-if="totalPages > 1"
        :page.sync="currentPage"
        :pages.sync="totalPages"
      />
    </div>
    <div v-else slot="mainContent" class="text-center error">{{ errorMessage }}</div>
  </div>
</template>

<script>

import {loyaltyByLocationsAPI} from "../../services/api/loyaltyByLocations";
import _ from "lodash";
import LoadingIcon from "../shared/LoadingIcon";
import SortTableHeader from "../shared/SortTableHeader";

export default {
  name: "LoyaltyByLocation",
  components: {
    LoadingIcon,
    SortTableHeader,
  },
  props: {
    isEditable: Boolean,
    hasLocations: Boolean,
    locationsDelta: Map,
  },
  data() {
    return {
      areLocationsLoading: false,
      columns: [
        {
          column: "display_name",
          name: "location",
          sort: null
        }, {
          column: "region",
          name: "state",
          sort: "asc"
        }
      ],
      currentPage: 0,
      errorMessage: null,
      locations: [],
      searchText: null,
      totalPages: null
    }
  },
  computed: {
    sortOptions() {
      return this.columns.find(value => value.sort !== null)
    },
    hasSearchLocations() {
      return !(this.searchText && this.locations.length === 0);
    },
  },
  async created() {
    this.debounce = _.debounce(this.debounceSearch, 500);
    await this.getLocations()
    this.$emit('update:hasLocations', !!this.locations?.length)
  },
  methods: {
    changeLocationsLoyalty(location) {
      location.loyalty_points = !location.loyalty_points
      const delta = {
        id: location.id,
        loyalty_points: location.loyalty_points
      }
      this.locationsDelta.set(location.id, delta)
    },
    changeSortOptions(sortValue) {
      return (!sortValue || sortValue === "desc") ? "asc" : "desc"
    },
    debounceSearch() {
      this.areLocationsLoading = true
      this.searchText = this.searchText.trim()
      this.currentPage = 0
      this.getLocations()
    },
    async getLocations() {
      try {
        this.areLocationsLoading = true
        const params = {
          params: {
            page: this.currentPage + 1,
            query: this.searchText,
            sort_column: this.sortOptions.column,
            sort_direction: this.sortOptions.sort
          }
        }
        const data = await loyaltyByLocationsAPI.GETLoyaltyByLocations(params)
        this.setLocations(data)
      }
      catch(error){
        this.errorMessage = `there is an api issue: ${error}`
      }
      finally{
        this.areLocationsLoading = false
      }
    },
    setLocations(data) {
      if (data) {
        this.errorMessage = null
        this.locations = data.merchant_locations
        this.totalPages = data.meta.total_pages
        this.locations.forEach(location => {
          if (this.locationsDelta.has(location.id)) {
            location.loyalty_points = this.locationsDelta.get(location.id).loyalty_points
          }
        })
      } else {
        this.errorMessage = "no found locations"
      }
    },
    sortLocation(sortId) {
      this.columns.forEach(value => {
        value.sort = value.column === sortId ? this.changeSortOptions(value.sort) : null
      });
      this.getLocations()
    },
  },
  watch: {
    currentPage: function() {
      this.getLocations()
    }
  }
}
</script>

<style lang="scss" scoped>
.location-search {
  border-radius: 9999px;
}
#location-table th:first-child {
  padding-left: 0;
}
</style>
